import * as lodash from "lodash";
import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";

import {
    ITCIConnection,
    ITCIConnectionRequest,
    ITCIConnectionResponse,
    ITCIConnector,
    TCIConnectionVisibility,
    TCI_CONNECTION_ACTION_TYPE
} from "@tci/common";
import { BaseTCIConnectionServiceProvider } from "../tci-tenant-services";

@Injectable()
export class TciTenantBaseConnectionSharedService extends BaseTCIConnectionServiceProvider {
    private handlers: { [key: string]: boolean } = {};
    // connStates: Stores the conn states to know when to publish an update
    connStates: { [key: string]: ITCIConnectionResponse } = {}; // AppId is key
    private connection$: Subject<ITCIConnectionResponse> = new Subject();

    protected publish(status: ITCIConnectionResponse) {
        if (!status.connectionId || this.handlers[status.connectionId]) {
            this.connection$.next(status);
        }
    }

    public canCreate(request: ITCIConnectionRequest): Observable<boolean> {
        return Observable.of(false);
    }

    public canDelete(request: ITCIConnectionRequest): Observable<boolean> {
        return Observable.of(false);
    }

    public canEdit(request: ITCIConnectionRequest): Observable<boolean> {
        return Observable.of(false);
    }

    public canSupport(connectorType: ITCIConnector): Observable<boolean> {
        return Observable.of(false);
    }

    public createConnection(request: ITCIConnectionRequest, returnUrl: string): Observable<ITCIConnectionResponse> {
        return null;
    }

    public deleteConnection(request: ITCIConnectionRequest): Observable<ITCIConnectionResponse> {
        return null;
    }

    /**
     * Show tooltip text only when canDelete() == false
     * @param request
     */
    getDeleteTooltip(request: ITCIConnectionRequest): Observable<string> {
        return Observable.of("");
    }

    /**
     * Indicate if this connection has status which may put a badge on the connection in
     * connection list page
     */
    hasConnectionStatus(request: ITCIConnectionRequest): Observable<boolean> {
        return Observable.of(false);
    }

    /**
     * Get the connection status msg for display
     * @param request
     */
    getConnectionStatus(request: ITCIConnectionRequest): Observable<ITCIConnectionResponse> {
        return Observable.of(undefined);
    }

    /**
     * Disconnected Components that take actions related to a connection
     * which impacts the Connection list UI state need to enclose the
     * actions within the callback so the connection provider can let
     * other dependencies get notified on the completion of the action
     * for performing refresh or any other action
     * @param input
     * @param callback
     */
    pushConnectionChanges(
        input: ITCIConnectionRequest,
        callback: (cinput: ITCIConnectionRequest) => Observable<ITCIConnectionResponse>) {
        return Observable.create(obs => {
            const newState: ITCIConnectionResponse = {
                connection: input.connection,
                connectionId: input.connection.id,
                connectorId: input.connection.connector.id
                // success: true,
                // notificationType: TCIApplicationDeploymentNotification.NOTIFICATION_PROGRESS,
                // status: new TCIApplicationDeploymentStatus("Preparing", TCIAppStatusCategory.IN_PROGRESS)
            } as ITCIConnectionResponse;

            const state = lodash.assign(lodash.cloneDeep(this.connStates[input.connection.id] || {}), newState);
            // this.publish(state, true);
            obs.next(state);

            callback(input) // Execute the callback function
                .finally(() => {
                    this.handlers[input.connection.id] = false;
                    const subscription = this.connection$ // Subscribe to the core publishing awaiting for response
                        .filter(res => res.connectionId === input.connection.id)
                        // .filter(res => res.notificationType === TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH)
                        .subscribe(res => { // Final state
                            obs.next(res);
                            obs.complete();
                            subscription.unsubscribe();
                        });
                })
                .subscribe(
                    cbRes => {
                        // if (cbRes.notificationType === TCIApplicationDeploymentNotification.NOTIFICATION_RESULT_APP_CHANGES) {
                        //     this.publish(cbRes, true);
                        // }
                        obs.next(cbRes); // Send callback response
                    },
                    error => obs.error(error)
                );
        });
    }


    /**
     * Return state if the provider is busy waiting for connection actions to finish
     * @param input
     */
    isBusy(input: ITCIConnectionRequest): Observable<boolean> {
        return Observable.of(false);
    }

    public editConnection(request: ITCIConnectionRequest, returnUrl: string): Observable<ITCIConnectionResponse> {
        return null;
    }

    public getConnectedAppCount(request: ITCIConnectionRequest): Observable<number> {
        return Observable.of(0);
    }

    public getConnectedApps(request: ITCIConnectionRequest): Observable<string[]> {
        return Observable.of([]);
    }

    public getConnectionsSubject(): Subject<ITCIConnectionResponse> {
        return this.connection$;
    }

    public getConnectors(): Observable<ITCIConnector[]> {
        return Observable.of([]);
    }

    /**
     * Returns a list of connections from individual provider
     * @return @Observable<ITCIConnection[]>
     */
    getConnections(): Observable<ITCIConnection[]> {
        return Observable.of([]);
    }

    public showConnectedApps(request: ITCIConnectionRequest): Observable<boolean> {
        return Observable.of(false);
    }

    public startConnectionSubscription(request: ITCIConnectionRequest): Observable<boolean> {
        this.handlers[request.connection.id] = true;
        return Observable.of(true);
    }

    public stopConnectionSubscription(request: ITCIConnectionRequest): Observable<boolean> {
        this.handlers[request.connection.id] = false;
        return Observable.of(true);
    }

    /**
     * Get currently handled connection ids
     */
    protected getHandledConnectionIds(): string[] {
        return Object.keys(this.handlers).filter(id => this.handlers[id] === true);
    }

    /**
     * The UI will show/hide visibility column based on this result true/false
     * The tenants e.g. Flogo TCI and Flogo Enterprise will have different results
     * @param input
     * @deprecated
     */
    canShowConnectionVisibility(input: ITCIConnectionRequest): Observable<boolean> {
        return Observable.of(true);
    }

    /**
     * Return the visibility status for a given {@ITCIConnection}
     * The changing of the visibility from hamburger menu code needs to be
     * wrapped inside pushConnectionChanges() which will trigger a refresh
     * of the Connection List via the NOTIFICATION_REFRESH and conn list
     * will call this provider function to get individual connection visibility
     * status
     * @param input
     * @deprecated
     */
    getConnectionVisibility(input: ITCIConnectionRequest): Observable<ITCIConnectionResponse> {
        return Observable.of({
            connectorId: input.connector && input.connector.id,
            connectionId: input.connection && input.connection.id,
            tenantType: input.connection && input.connection.tenantType,
            actionType: TCI_CONNECTION_ACTION_TYPE.CONN_CHANGE_VISIBILITY,
            visibilityType: TCIConnectionVisibility.UNKNOWN
        });
    }
}
